// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';
import { CustomCheckBox } from 'components/atoms/CustomCheckbox';
import ResponsiveHistoryTable, {
  CustomColumn,
} from 'components/molecules/ResponsiveHistoryTable';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import TableFilterMultiSelect from 'components/molecules/TableFilterMultiSelect';
import QRScanningModal from 'components/organisms/QRScanningModal';
import { QRScanningProps } from 'components/organisms/QRScanning';
import { IconCheck } from 'components/atoms/icons/IconCheck';
import { IconEyeSlash } from 'components/atoms/icons/IconEyeSlash';
import { getBrightnessFromColor, normalizeNumber } from 'util/commons';
import { SelectField } from 'components/molecules/SelectField';
import {
  getStorageSiteFromSites,
  getStorageSiteMaterialNames,
  getStorageSiteReceivedItems,
  registerStorageSite,
  scanStorageSiteQr,
} from 'apis/site_storage';
import { getCommonTemporaryStorages } from 'apis/commons';
import { toast } from 'react-toastify';
import styles from './index.module.scss';
import {
  StorageSiteReceivedItemResponseType,
  StorageSiteScanQrItemType,
  TemporaryStorageType,
} from 'types/siteStorage';
import { getFormatDateWithDeviceTimezone } from 'ts/formatDate';
import { TEMPORARY_STORAGE_COLORS_PALETTE } from 'util/ConstantValues';
import Popover from 'components/atoms/Popover';

type StorageLocationRow = StorageSiteReceivedItemResponseType & {
  checkbox: any;
};

type Filter = {
  receivedAt?: [string, string];
  fromSiteIds?: string[];
  materialNames?: string[];
};

const NOT_SET_OPTION_VALUE = 'NOT_SET';

type StorageLocationFormValues = {
  storageLocation: string;
  qrStorageLocation: string;
};

const StorageLocationRegistrationScreen = () => {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isOpenQRModal, setIsOpenQRModal] = useState(false);
  const [selectedStorageLocationId, setSelectedStorageLocationId] =
    useState('');
  const [qrStorageLocationId, setQrStorageLocationId] = useState('');
  const [filter, setFilter] = useState<Filter>({});

  const [items, setItems] = useState<StorageLocationRow[]>([]);
  const [storageLocations, setStorageLocations] = useState<
    TemporaryStorageType[]
  >([]);
  const [fromSites, setFromSites] = useState<{ id: string; name: string }[]>(
    []
  );
  const [materialNames, setMaterialNames] = useState<string[]>([]);

  useEffect(() => {
    getCommonTemporaryStorages({
      successCallback: (data) => {
        setStorageLocations(data);
      },
    });
  }, []);

  useEffect(() => {
    getStorageSiteFromSites({
      successCallback: (data) => setFromSites(data),
    });
  }, []);

  useEffect(() => {
    getStorageSiteMaterialNames({
      successCallback: (data) => setMaterialNames(data.map((d) => d.name)),
    });
  }, []);

  const fetchItems = useCallback(() => {
    getStorageSiteReceivedItems({
      dateFrom: filter.receivedAt?.[0],
      dateTo: filter.receivedAt?.[1],
      materialNames: filter.materialNames,
      fromSiteIds: filter.fromSiteIds,
      page: 0,
      successCallback: (data) => {
        setItems(data.map((item) => ({ ...item, checkbox: null })));
      },
    });
  }, [filter.fromSiteIds, filter.materialNames, filter.receivedAt]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => items.some((item) => item.resourceId === id))
    );
  }, [items]);

  const fromOptions = useMemo(
    () => fromSites.map((site) => ({ value: site.id, label: site.name })),
    [fromSites]
  );

  const materialNameOptions = useMemo(
    () => materialNames.map((name) => ({ value: name, label: name })),
    [materialNames]
  );

  const storageLocationOptions = useMemo(
    () => [
      {
        value: NOT_SET_OPTION_VALUE,
        label: t('storage_location_registration.not_set'),
      },
      ...storageLocations.map((loc) => ({
        value: loc.id,
        label: loc.name,
      })),
    ],
    [storageLocations, t]
  );

  const handleSelectAll = () => {
    if (!items.length) return;

    setSelectedIds((prev) => {
      const filteredIds = items.map((item) => item.resourceId);
      const isAllFilteredSelected = filteredIds.every((id) =>
        prev.includes(id)
      );

      if (isAllFilteredSelected) {
        return prev.filter((id) => !filteredIds.includes(id));
      }

      return Array.from(new Set([...prev, ...filteredIds]));
    });
  };

  const handleCheckItem = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const getStorageLocationMeta = (
    storageLocation: StorageSiteReceivedItemResponseType['storageLocation']
  ) => {
    if (!storageLocation)
      return { name: undefined, backgroundColor: undefined, color: undefined };

    const backgroundColor =
      TEMPORARY_STORAGE_COLORS_PALETTE[
        Number(storageLocation.color) % TEMPORARY_STORAGE_COLORS_PALETTE.length
      ];

    return {
      name: storageLocation.name,
      backgroundColor,
      color:
        getBrightnessFromColor(backgroundColor) <= 100 ? '#ffffff' : '#1b1b1b',
    };
  };

  const handleSave =
    (
      form: FormApi<
        StorageLocationFormValues,
        Partial<StorageLocationFormValues>
      >
    ) =>
    () => {
      if (!selectedIds.length) {
        return;
      }

      registerStorageSite({
        processingObjectIds: selectedIds,
        temporaryStorageId:
          selectedStorageLocationId &&
          selectedStorageLocationId !== NOT_SET_OPTION_VALUE
            ? Number(selectedStorageLocationId)
            : null,
        successCallback: () => {
          setSelectedIds([]);
          setSelectedStorageLocationId('');
          form.mutators.setValue('storageLocation', '');
          fetchItems();
          toast.success(t('messages.M_011'));
        },
      });
    };

  const handleQRSubmit =
    (
      form: FormApi<
        StorageLocationFormValues,
        Partial<StorageLocationFormValues>
      >
    ): QRScanningProps<StorageSiteScanQrItemType>['onSubmit'] =>
    ({ checkedItemIds, groupedItems, onSuccess }) => {
      if (!checkedItemIds.length) return;

      registerStorageSite({
        processingObjectIds: checkedItemIds,
        temporaryStorageId:
          qrStorageLocationId && qrStorageLocationId !== NOT_SET_OPTION_VALUE
            ? Number(qrStorageLocationId)
            : null,
        successCallback: () => {
          const remainingCount = groupedItems
            .flatMap((g) => g.data)
            .filter((item) => !checkedItemIds.includes(item.id)).length;

          onSuccess();
          fetchItems();
          toast.success(t('messages.M_109'));
          setQrStorageLocationId('');
          form.mutators.setValue('qrStorageLocation', '');

          if (remainingCount === 0) {
            setIsOpenQRModal(false);
          }
        },
      });
    };

  const handleScan: QRScanningProps<StorageSiteScanQrItemType>['onScan'] =
    async (qrCode, addItem) => {
      await scanStorageSiteQr({
        qrCode,
        successCallback: (data) => {
          if (data) {
            toast.success(t('qr_scan.success'));
            addItem(data);
          }
        },
      });
    };

  const selectedAllIds = useMemo(
    () => items.map((item) => item.resourceId),
    [items]
  );

  const isAllSelected =
    selectedAllIds.length > 0 &&
    selectedAllIds.every((id) => selectedIds.includes(id));

  const columns: CustomColumn<StorageLocationRow>[] = useMemo(
    () => [
      {
        Header: (
          <span
            onClick={handleSelectAll}
            style={{
              cursor: items.length > 0 ? 'pointer' : 'not-allowed',
            }}
          >
            {t(isAllSelected ? 'common.deselect_all' : 'common.select_all')}
          </span>
        ),
        accessor: 'checkbox' as any,
        className: styles.checkboxColumn,
        Cell: ({ row }) => (
          <CustomCheckBox
            id={row.original.resourceId}
            darkMode
            checked={selectedIds.includes(row.original.resourceId)}
            onChange={() => handleCheckItem(row.original.resourceId)}
          />
        ),
      },
      {
        Header: (
          <TableFilterCalendar
            label={t('storage_location_registration.received_at') || ''}
            value={filter.receivedAt}
            onChange={(value) =>
              setFilter((prev) => ({ ...prev, receivedAt: value }))
            }
            labelClassName={styles.tableFilterLabel}
            endDateRequired
          />
        ),
        accessor: 'receivedDatetime',
        Cell: ({ value }) => <>{getFormatDateWithDeviceTimezone(value)}</>,
      },
      {
        Header: (
          <TableFilterMultiSelect
            label={t('storage_location_registration.from') || ''}
            options={fromOptions}
            values={filter.fromSiteIds}
            onChange={(value) =>
              setFilter((prev) => ({ ...prev, fromSiteIds: value }))
            }
            classNames={{ label: styles.tableFilterLabel }}
          />
        ),
        accessor: 'fromPlace',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('storage_location_registration.resource_id') || '',
        accessor: 'resourceId',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: (
          <TableFilterMultiSelect
            label={t('storage_location_registration.material_name') || ''}
            options={materialNameOptions}
            values={filter?.materialNames}
            onChange={(value) =>
              setFilter((prev) => ({ ...prev, materialNames: value }))
            }
            classNames={{ label: styles.tableFilterLabel }}
          />
        ),
        accessor: 'materialName',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('storage_location_registration.weight') || '',
        accessor: 'weight',
        Cell: ({ value }) => <>{`${value}kg`}</>,
      },
      {
        Header: t('storage_location_registration.local_storage') || '',
        accessor: 'storageLocation' as any,
        Cell: ({ row }) => {
          const { name, backgroundColor, color } = getStorageLocationMeta(
            row.original.storageLocation
          );

          return (
            <span
              className={styles.storageLocationBadge}
              style={{ backgroundColor, color, padding: name ? undefined : 0 }}
            >
              <Popover
                autoGen={{
                  lines: 1,
                  odd: 4,
                  wrapperStyle: {
                    width: 'fit-content',
                  },
                }}
                popUpStyle={{
                  fontSize: 18,
                }}
                positions={['top']}
                content={name ?? t('storage_location_registration.not_set')}
                containerStyle={{
                  maxWidth: '400px',
                  wordBreak: 'break-all',
                  zIndex: '1000',
                }}
              >
                <span>
                  {name ?? t('storage_location_registration.not_set')}
                </span>
              </Popover>
            </span>
          );
        },
      },
    ],
    [
      t,
      items,
      selectedIds,
      isAllSelected,
      filter,
      fromOptions,
      materialNameOptions,
    ]
  );

  return (
    <Form<StorageLocationFormValues>
      onSubmit={() => {}}
      mutators={{
        setValue: ([field, value], state, { changeValue }) => {
          changeValue(state, field, () => value);
        },
      }}
    >
      {({ form }) => (
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.filterForMobile}>
              <TableFilterCalendar
                label={t('storage_location_registration.received_at') || ''}
                value={filter.receivedAt}
                onChange={(value) =>
                  setFilter((prev) => ({ ...prev, receivedAt: value }))
                }
                expanded
                portalPositionPoint={(ref) => ref}
                endDateRequired
              />
              <TableFilterMultiSelect
                label={t('storage_location_registration.from') || ''}
                options={fromOptions}
                values={filter.fromSiteIds}
                onChange={(value) =>
                  setFilter((prev) => ({ ...prev, fromSiteIds: value }))
                }
                fullWidth
                expanded
                portalPositionPoint={(ref) => ref}
              />
              <TableFilterMultiSelect
                label={t('storage_location_registration.material_name') || ''}
                options={materialNameOptions}
                values={filter.materialNames}
                onChange={(value) =>
                  setFilter((prev) => ({ ...prev, materialNames: value }))
                }
                fullWidth
                expanded
                portalPositionPoint={(ref) => ref}
              />
            </div>

            <div className={styles.tableContainer}>
              <ResponsiveHistoryTable<StorageLocationRow>
                data={items}
                columns={columns}
                className={styles.historyTable}
                scrollResetDependencies={[filter, items]}
                isInheritHeight
              />
            </div>
          </div>

          <footer className={styles.footer}>
            <button
              className={styles.qrButton}
              onClick={() => setIsOpenQRModal(true)}
            >
              {t('storage_location_registration.qr_scan')}
            </button>
            <div className={styles.footerRight}>
              <SelectField
                name="storageLocation"
                value={selectedStorageLocationId}
                placeholder={t('storage_location_registration.local_storage')}
                options={storageLocationOptions}
                isDarkmode
                menuPlacement="top"
                showPlaceholderOption
                onChange={(value) =>
                  setSelectedStorageLocationId(String(value))
                }
                containerClassName={styles.storageLocationSelect}
              />
              <button
                className={styles.saveButton}
                disabled={
                  selectedIds.length === 0 || !selectedStorageLocationId
                }
                onClick={handleSave(form)}
              >
                {t('storage_location_registration.save')}
              </button>
            </div>
          </footer>

          {isOpenQRModal && (
            <QRScanningModal<
              StorageSiteScanQrItemType,
              StorageSiteScanQrItemType
            >
              classNames={{ scannedList: styles.qrScannedList }}
              title={t('storage_location_registration.title') as string}
              onScan={handleScan}
              onSubmit={handleQRSubmit(form)}
              getItemId={(data) => data.id}
              getLatestScanDisplay={(data) => ({
                label: t(
                  'storage_location_registration.material_name'
                ) as string,
                name: data.materialName,
                value: normalizeNumber({ value: data.weight }),
                unit: 'KG',
              })}
              renderGroupItem={({ item, isChecked, onToggle }) => (
                <div className={styles.scannedItem} onClick={onToggle}>
                  <div className={styles.scannedItemLeft}>
                    <div className={styles.iconContainer}>
                      {isChecked ? (
                        <div className={styles.iconCheck}>
                          <IconCheck size={20} />
                        </div>
                      ) : (
                        <IconEyeSlash size={26} />
                      )}
                    </div>
                    <div className={styles.scannedItemInfo}>
                      <span className={styles.scannedItemMaterial}>
                        {item.materialName}
                      </span>
                      <span className={styles.scannedItemId}>{item.id}</span>
                    </div>
                  </div>
                  <div className={styles.scannedItemRight}>
                    <div className={styles.scannedItemWeight}>
                      <span className={styles.weightLabel}>
                        {t('storage_location_registration.weight')}
                      </span>
                      <span className={styles.weightValue}>
                        <span>{normalizeNumber({ value: item.weight })}</span>
                        <span className={styles.weightUnit}>kg</span>
                      </span>
                    </div>
                    <div className={styles.scannedItemStorage}>
                      <span className={styles.weightLabel}>
                        {t('storage_location_registration.local_storage')}
                      </span>
                      {(() => {
                        const { name, backgroundColor, color } =
                          getStorageLocationMeta(item.storageLocation);
                        return (
                          <span
                            className={styles.storageLocationBadge}
                            style={{
                              backgroundColor,
                              color,
                              padding: name ? undefined : 0,
                            }}
                          >
                            {name ?? t('storage_location_registration.not_set')}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
              onClose={() => {
                setIsOpenQRModal(false);
                setQrStorageLocationId('');
                form.mutators.setValue('qrStorageLocation', '');
              }}
              renderBottomActions={({ isDisabled, submit }) => (
                <div className={styles.qrBottomActions}>
                  <SelectField
                    name="qrStorageLocation"
                    value={qrStorageLocationId}
                    placeholder={t(
                      'storage_location_registration.local_storage'
                    )}
                    options={storageLocationOptions}
                    isDarkmode
                    menuPlacement="top"
                    usePortal
                    showPlaceholderOption
                    onChange={(value) => setQrStorageLocationId(String(value))}
                    containerClassName={styles.storageLocationSelect}
                  />
                  <button
                    className={styles.saveButton}
                    disabled={isDisabled || !qrStorageLocationId}
                    onClick={() => submit()}
                  >
                    {t('storage_location_registration.save')}
                  </button>
                </div>
              )}
            />
          )}
        </div>
      )}
    </Form>
  );
};

export default StorageLocationRegistrationScreen;
